import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import LevelEndType from "../data/LevelEndType";
import GameConfig from "./GameConfig";
import GameStatus from "./GameStatus";
import PuzzleModel from "./PuzzleModel";
import TrackingController from "./TrackingController";

export default class GameModel extends EventDispatcher {

    /**
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController 
     */
    constructor(dispatcher, gameConfig, trackingController) {
        super();
        this._contextDispatcher = dispatcher;
        this._config = gameConfig;
        this._trackingController = trackingController;

        const speedParam = parseFloat(getURLParameter("speed"));
        this._speedfactor = isNaN(speedParam) ? 1 : speedParam;
        this.lastPuzzleSuccessful = false;
        this.tutorialAlreadySeen = false;
    }

    dispatchPlatformProgressEvent(progressValue) {
        this._trackingController.trackProgress(progressValue, this.elapsedTime);
    }

    initialize() {
        this.reset();
    }

    reset() {
        this._puzzleIndex = -1;
        this._elapsedTime = 0;
        this.score = 0;
        this.mistakeCount = 0;
    }

    set restoredFeedbackPending(bool) {
        this._restoredFeedbackPending = bool;
    }
    get restoredFeedbackPending() {
        return this._restoredFeedbackPending;
    }

    set tutorialAlreadySeen(bool) {
        this._tutorialAlreadySeen = bool;
    }

    get tutorialAlreadySeen() {
        return this._tutorialAlreadySeen;
    }

    get speedFactor() {
        return this._speedfactor;
    }

    startTutorial() {
        //Start with first practice level
        this.status = GameStatus.TUTORIAL;
        this.score = 0;
        this.mistakeCount = 0;
        this._puzzleIndex = -1;
        this.initTutorialPuzzle();
    }

    startPractice() {
        //Start with first practice level
        this.status = GameStatus.PRACTICE;
        this.score = 0;
        this.mistakeCount = 0;
        this._puzzleIndex = -1;
        this.doNextPuzzle();
    }

    start() {
        //Start with first level
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.status = GameStatus.RUNNING;
        this.score = 0;
        this.mistakeCount = 0;
        this._puzzleIndex = -1;
        this.doNextPuzzle();
    }

    restore(obj) {
        //Restore puzzle
        this.elapsedTime = obj.elapsed;
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this._restoredFeedbackPending = true;
        this.initPuzzle(obj.index, obj.puzzle, obj.currentSequence);
        //restore scores
        this.score = obj.score;
        this.mistakeCount = obj.mistakeCount;
        this.status = GameStatus.RUNNING;
    }

    step(dt) {
        this.elapsedTime += dt;
        if (this._enableTimer) {
            if (this.remainingTime <= 0) {
                this.timeout();
            } else {
                if (this._lastTickingTimer - this.remainingTime > this._config.getValue("timings.delayRefreshTimer")) {
                    this.updateTimer();
                    this._lastTickingTimer = this.remainingTime;
                }
            }
        }
    }

    timeout() {
        this._enableTimer = false;
        this.dispatchEvent(GameModel.TIMEOUT);
    }

    updateTimer() {
        var percentTimer = Math.floor(this.remainingTime / (this._puzzleModel.timer * 1000) * 100);
        percentTimer = (percentTimer < 0 ? 0 : percentTimer);
        this.dispatchEvent(GameModel.PERCENT_TIMER, percentTimer);
    }

    stopTimer() {
        this._elapsedTime = 0;
        this._lastTickingTimer = 0;
        this._enableTimer = false;
    }

    startTimer() {
        this._enableTimer = true;
        this._elapsedTime = 0;
        const timerValue = this._puzzleModel.timer;
        this._availableTime = this._elapsedTime + timerValue * 1000;
        this._lastTickingTimer = this.remainingTime;
    }

    set elapsedTime(value) {
        this._elapsedTime = value;
        this.dispatchEvent(GameModel.ELAPSED_TIME, this._elapsedTime);
    }
    get elapsedTime() {
        return this._elapsedTime;
    }
    get remainingTime() {
        return this._availableTime - this._elapsedTime;
    }

    set status(value) {
        this._status = value;
        this.dispatchEvent(GameModel.STATUS, value);
    }
    get status() {
        return this._status;
    }

    hasNextPracticePuzzle() {
        return this._puzzleIndex < this._config.practicePuzzles.length - 1;
    }

    hasNextPuzzle() {
        return this._puzzleIndex < this._config.puzzles.length - 1;
    }

    isPuzzleAllowFail() {
        if (this._puzzleModel === undefined) {
            return true;
        }
        return this._puzzleModel.allowFail;
    }

    retryPuzzle() {
        if (this.canRetryPuzzle()) {
            this.initPuzzle(this._puzzleIndex, null, this._puzzleModel.currentSequence + 1);
            return true;
        }
        return false;
    }

    canRetryPuzzle() {
        if (!this._puzzleModel.allowFail && this._puzzleModel.hasNextSequence()) {
            return true;
        }
        return false;
    }

    doNextPuzzle() {
        this.initPuzzle(this._puzzleIndex + 1);
    }

    initTutorialPuzzle() {
        this._puzzleModel = this._createPuzzleModel(this._config.getValue("tutorial.puzzle"));
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    isPreviousPondDifferent() {
        if (this.status != GameStatus.PRACTICE && this.hasNextPuzzle() && this._puzzleIndex > 0 && this._config.getPuzzle(this._puzzleIndex).location != this._config.getPuzzle(this._puzzleIndex - 1).location) {
            return true;
        }
        return false;
    }

    /**
     * 
     * @param {Number} index 
     * @param {Object} puzzleState - generic object containing savegame data for puzzle
     */
    initPuzzle(index, puzzleState = null, currentSequence = 0) {
        this._destroyPuzzle();

        // Looping back to first puzzle when end is reached
        const nbrPuzzles = this.status == GameStatus.PRACTICE ? this._config.practicePuzzles.length : this._config.puzzles.length;
        this._puzzleIndex = (index >= nbrPuzzles ? 0 : index);
        const puzzleCfg = this.status == GameStatus.PRACTICE ? this._config.getPracticePuzzle(index) : this._config.getPuzzle(index);
        this._puzzleModel = this._createPuzzleModel(puzzleCfg, currentSequence);
        this._addPuzzleEventListeners();
        if (puzzleState) {
            this._puzzleModel.setFromObject(puzzleState);
        }
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    _createPuzzleModel(puzzleCfg, currentSequence = 0) {
        return new PuzzleModel(puzzleCfg.location, puzzleCfg.allowFail, puzzleCfg.sequence, puzzleCfg.rule, puzzleCfg.timings, puzzleCfg.timer, currentSequence);
    }

    _destroyPuzzle() {
        if (this._puzzleModel) {
            this._puzzleModel.removeEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
            this._puzzleModel = null;
        }
    }

    _addPuzzleEventListeners() {
        this._puzzleModel.addEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
    }

    selectItem(spot) {
        this.puzzle.pickSpot(spot);

        if (this._shouldTrack()) {
            this._trackingController.trackSpotSelected(this.puzzleNumber, spot.id, this.elapsedTime);
        }
    }

    deselectItem(spot) {
        this.puzzle.unpickSpot(spot);
        if (this._shouldTrack()) {
            this._trackingController.trackSpotDeselected(this.puzzleNumber, spot.id, this.elapsedTime);
        }
    }

    startPuzzleInteraction() {
        this.elapsedTime = 0;
        if (this._shouldTrack()) {
            this._trackingController.trackLevelStart(this.puzzleNumber, this.elapsedTime);
        }
    }
    submitPuzzle(timeout = false) {
        let submittedSequence = this._puzzleModel.pickedSpots.map(s => s.id);
        let isSuccessful = this._puzzleModel.validate();
        let submittedBy = (timeout ? LevelEndType.TIMEOUT : LevelEndType.USER);
        if (timeout) {
            isSuccessful = false;
        }

        if (this._shouldTrack()) {
            this._trackingController.trackLevelSubmitted(
                this.puzzleNumber,
                isSuccessful,
                submittedSequence,
                this.elapsedTime,
                this._puzzleModel.currentSequence + 1,
                submittedBy
            );
            this.dispatchEvent(GameModel.SUBMIT);
        }
        this.lastPuzzleSuccessful = isSuccessful;
        return isSuccessful;
    }

    wasLastPuzzleSuccessful() {
        return this.lastPuzzleSuccessful;
    }

    get currentSequence() {
        return this._puzzleModel.currentSequence;
    }

    get puzzle() {
        return this._puzzleModel;
    }

    get puzzleIndex() {
        return this._puzzleIndex;
    }

    get puzzleNumber() {
        return this._puzzleIndex + 1;
    }

    _shouldTrack() {
        return this.status != GameStatus.TUTORIAL && this.status != GameStatus.PRACTICE;
    }

};


GameModel.STATUS = "GameModel.STATUS";
GameModel.PUZZLE = "GameModel.PUZZLE";
GameModel.SUBMIT = "GameModel.SUBMIT";
GameModel.SCORE = "GameModel.SCORE";
GameModel.MISTAKES = "GameModel.MISTAKES";
GameModel.ELAPSED_TIME = "GameModel.TIME";
GameModel.PERCENT_TIMER = "GameModel.PERCENT_TIMER";
GameModel.TIMEOUT = "GameModel.TIMEOUT";

GameModel.MATCH = "GameModel.MATCH";
GameModel.MISMATCH = "GameModel.MISMATCH";

GameModel.TUTORIAL_SEEN = "GameModel.TUTORIAL_SEEN";
GameModel.CLOSE_TUTORIAL_POPUP = "GameModel.CLOSE_TUTORIAL_POPUP";